import { WeeklyWindow } from "./types";

/** Monday first, matching the backend's day_of_week where 0 is Monday. */
export const WEEKDAY_KEYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
] as const;

export interface WeekdayRow {
    dayOfWeek: number;
    dayKey: (typeof WEEKDAY_KEYS)[number];
    windows: WeeklyWindow[];
}

/**
 * Group the published windows by weekday, dropping days with none.
 *
 * A therapist who works Tuesdays and Thursdays should see two rows, not seven
 * with five of them blank — the empty days say nothing a reader needs.
 */
export function groupByWeekday(windows: WeeklyWindow[]): WeekdayRow[] {
    const byDay = new Map<number, WeeklyWindow[]>();

    for (const window of windows) {
        if (window.day_of_week < 0 || window.day_of_week > 6) continue;
        const existing = byDay.get(window.day_of_week) ?? [];
        existing.push(window);
        byDay.set(window.day_of_week, existing);
    }

    return [...byDay.entries()]
        .sort(([a], [b]) => a - b)
        .map(([dayOfWeek, dayWindows]) => ({
            dayOfWeek,
            dayKey: WEEKDAY_KEYS[dayOfWeek],
            windows: [...dayWindows].sort((a, b) =>
                a.start_time.localeCompare(b.start_time),
            ),
        }));
}

/** "09:00" -> 9.0, "16:30" -> 16.5. Used to place a band on the day strip. */
export function toDecimalHour(time: string): number {
    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours)) return 0;
    return hours + (Number.isNaN(minutes) ? 0 : minutes) / 60;
}

export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 22;

/**
 * Where a window sits on the strip, as percentages, clamped to the visible day.
 * A window running past midnight is clamped rather than wrapped: the strip
 * shows one day, and a band that wrapped would read as two.
 */
export function bandPosition(window: WeeklyWindow): { left: number; width: number } {
    const span = DAY_END_HOUR - DAY_START_HOUR;
    const rawStart = toDecimalHour(window.start_time);
    const rawEnd = toDecimalHour(window.end_time);

    const start = Math.min(Math.max(rawStart, DAY_START_HOUR), DAY_END_HOUR);
    const end = Math.min(Math.max(rawEnd, start), DAY_END_HOUR);

    return {
        left: ((start - DAY_START_HOUR) / span) * 100,
        width: Math.max(((end - start) / span) * 100, 4),
    };
}
